import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, Mail, Phone, User, AlertCircle, CheckCircle,
  Clock, Tag, Flag, X, FileText, MessageSquare, Calendar, Hash, ArrowLeft
} from 'lucide-react';
import { adminVerify } from '../../services/api';
import { listGrievances, updateGrievanceStatus, updateGrievance } from '../../services/api';
const statusOptions = ['All', 'Open', 'In Progress', 'Resolved', 'Closed'];
const priorityOptions = ['All', 'Low', 'Medium', 'High', 'Urgent', 'Critical'];

const AdminGrievancesPage = () => {
  const navigate = useNavigate();
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [filters, setFilters] = useState({
    ticketNumber: '',
    title: '',
    submittedBy: '',
    email: '',
    category: '',
    status: '',
    priority: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  const [selected, setSelected] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isUpdatingResolution, setIsUpdatingResolution] = useState(false);
  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
      } catch (e) {
        navigate('/admin/login');
      }
    };
    init();
  }, [navigate]);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key] || params[key] === 'All') delete params[key];
      });
      
      const data = await listGrievances(params);
      
      // Handle response format
      let grievancesList = [];
      let paginationInfo = null;
      
      if (Array.isArray(data)) {
        grievancesList = data;
      } else if (data.grievances) {
        grievancesList = data.grievances;
        paginationInfo = {
          total: data.total || 0,
          totalPages: data.totalPages || 0,
          page: data.page || 1
        };
      } else {
        grievancesList = [];
      }
      
      setGrievances(grievancesList);
      
      // Update pagination if data has pagination info
      if (paginationInfo) {
        setPagination(prev => ({
          ...prev,
          total: paginationInfo.total,
          totalPages: paginationInfo.totalPages
        }));
      }
    } catch (e) {
      setError(e.message || 'Failed to load grievances');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const handleStatusChange = async (id, status) => {
    try {
      await updateGrievanceStatus(id, status);
      setSuccess(`Grievance status updated to ${status}`);
      await refresh();
      if (selected && selected._id === id) {
        const updated = grievances.find(g => g._id === id);
        if (updated) {
          setSelected({ ...updated });
        }
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to update status');
    }
  };

  const handleSaveResolution = async () => {
    if (!selected) return;
    setIsUpdatingResolution(true);
    try {
      await updateGrievance(selected._id, { 
        resolution: resolutionNotes,
        resolvedAt: resolutionNotes ? new Date() : undefined
      });
      setSuccess('Resolution notes saved successfully');
      await refresh();
      const updated = grievances.find(g => g._id === selected._id);
      if (updated) {
        setSelected({ ...updated, resolution: resolutionNotes });
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Failed to save resolution notes');
    } finally {
      setIsUpdatingResolution(false);
    }
  };

  // Grievances are already filtered by backend
  const filteredGrievances = grievances;

  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return value;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open': return 'bg-red-100 text-red-800';
      case 'In Progress': return 'bg-amber-100 text-amber-800';
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Closed': return 'bg-gray-200 text-gray-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-blue-100 text-blue-800';
      case 'High': return 'bg-amber-100 text-amber-800';
      case 'Urgent': return 'bg-orange-100 text-orange-800';
      case 'Critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  useEffect(() => {
    if (selected) {
      setResolutionNotes(selected.resolution || '');
    }
  }, [selected]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Grievances'}</h1>
              <p className="text-xs text-gray-500">Monitor and manage all submitted grievances</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <span className="font-medium">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">{'Filters'}</h3>
              {Object.values(filters).some(f => f) && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {Object.values(filters).filter(f => f).length} active
                </span>
              )}
            </div>
            {Object.values(filters).some(f => f) && (
              <button
                onClick={() => {
                  setFilters({
                    ticketNumber: '',
                    title: '',
                    submittedBy: '',
                    email: '',
                    category: '',
                    status: '',
                    priority: '',
                    startDate: '',
                    endDate: ''
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                {'Clear All'}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {/* Ticket Number Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Ticket Number</label>
              <div className="relative">
                <Hash className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.ticketNumber ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search ticket number..."
                  value={filters.ticketNumber}
                  onChange={(e) => handleFilterChange('ticketNumber', e.target.value)}
                />
                {filters.ticketNumber && (
                  <button
                    onClick={() => handleFilterChange('ticketNumber', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Title Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Title</label>
              <div className="relative">
                <FileText className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.title ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search title..."
                  value={filters.title}
                  onChange={(e) => handleFilterChange('title', e.target.value)}
                />
                {filters.title && (
                  <button
                    onClick={() => handleFilterChange('title', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Submitted By Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Submitted By</label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.submittedBy ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search name..."
                  value={filters.submittedBy}
                  onChange={(e) => handleFilterChange('submittedBy', e.target.value)}
                />
                {filters.submittedBy && (
                  <button
                    onClick={() => handleFilterChange('submittedBy', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Email Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.email ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search email..."
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                />
                {filters.email && (
                  <button
                    onClick={() => handleFilterChange('email', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Category</label>
              <div className="relative">
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg px-3 ${filters.category ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search category..."
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                />
                {filters.category && (
                  <button
                    onClick={() => handleFilterChange('category', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                {statusOptions.filter(opt => opt !== 'All').map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Priority</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <option value="">All Priorities</option>
                {priorityOptions.filter(opt => opt !== 'All').map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">From Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">To Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                min={filters.startDate}
              />
            </div>
          </div>

        </div>


        {/* List */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Grievances</h3>
              <p className="text-sm text-gray-500 mt-1">
                {pagination.total || filteredGrievances.length} grievance{(pagination.total || filteredGrievances.length) !== 1 ? 's' : ''} total
              </p>
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-3"></div>
              <p className="text-gray-600">Loading grievances...</p>
            </div>
          ) : filteredGrievances.length === 0 ? (
            <div className="p-10 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-1 font-medium">No grievances found.</p>
              <p className="text-sm text-gray-500">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredGrievances.map((g) => (
                <button
                  key={g._id}
                  type="button"
                  onClick={() => setSelected(g)}
                  className="w-full text-left p-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all flex items-start justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {g.ticketNumber || '—'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(g.status)}`}>
                            {g.status || 'Open'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityBadgeClass(g.priority)}`}>
                            {g.priority || 'Medium'}
                          </span>
                        </div>
                        <h4 className="font-semibold text-gray-900 text-base mb-1">{g.title}</h4>
                        <p className="text-xs text-gray-600 uppercase tracking-wide flex items-center gap-1">
                          <Tag className="h-3 w-3" /> {g.category}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600 mt-2">
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-gray-400" />
                        <span>{g.submittedBy}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-gray-400" />
                        <span>{g.email}</span>
                      </div>
                      {g.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3 w-3 text-gray-400" />
                          <span>{g.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span>{formatDateTime(g.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total || filteredGrievances.length} total)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Details Modal */}
      {selected && (
        <div className="fixed inset-0 z-[1000] flex items-stretch sm:items-center justify-end bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white shadow-2xl w-full sm:w-[92vw] md:w-[82vw] lg:w-[70vw] xl:w-[60vw] 2xl:w-[52vw] max-w-[960px] h-full sm:h-[calc(100vh-32px)] sm:my-4 flex flex-col rounded-none sm:rounded-2xl overflow-x-hidden min-w-0">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Grievance Details</h3>
                <p className="text-xs text-gray-500">Ticket: {selected.ticketNumber || '—'}</p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Submitted By</div>
                  <div className="flex items-center gap-2 font-medium text-gray-900">
                    <User className="h-4 w-4 text-gray-400" />
                    {selected.submittedBy}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Email</div>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Mail className="h-4 w-4 text-gray-400" />
                    {selected.email}
                  </div>
                </div>
                {selected.phone && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Phone</div>
                    <div className="flex items-center gap-2 text-gray-900">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {selected.phone}
                    </div>
                  </div>
                )}
                {selected.company && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Company / Organization</div>
                    <div className="text-sm text-gray-900">{selected.company}</div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Category</div>
                  <div className="flex items-center gap-2 text-gray-900">
                    <Tag className="h-4 w-4 text-gray-400" />
                    {selected.category}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Priority</div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getPriorityBadgeClass(selected.priority)}`}>
                    <Flag className="h-3 w-3 mr-1" />
                    {selected.priority}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <select
                    className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    value={selected.status || 'Open'}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      await handleStatusChange(selected._id, newStatus);
                      setSelected((prev) => prev ? { ...prev, status: newStatus } : prev);
                    }}
                  >
                    {statusOptions.filter(s => s !== 'All').map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Subject</div>
                <div className="font-semibold text-gray-900">{selected.title}</div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Description</div>
                <div className="mt-1 p-4 bg-gray-50 rounded-xl text-sm text-gray-800 whitespace-pre-wrap">
                  {selected.description}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Created At</div>
                  <div className="text-gray-800">{formatDateTime(selected.createdAt)}</div>
                </div>
                {selected.resolvedAt && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Resolved At</div>
                    <div className="text-gray-800">{formatDateTime(selected.resolvedAt)}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-2 font-semibold">Resolution Notes</div>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                  rows={5}
                  placeholder="Add resolution notes or response details..."
                />
                <button
                  onClick={handleSaveResolution}
                  disabled={isUpdatingResolution}
                  className="mt-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingResolution ? 'Saving...' : 'Save Resolution'}
                </button>
              </div>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50 rounded-b-2xl">
              <div className="text-xs text-gray-500">
                Last updated: {formatDateTime(selected.updatedAt || selected.createdAt)}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <a
                  href={`mailto:${selected.email}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </a>
                {selected.phone && (
                  <a
                    href={`tel:${selected.phone}`}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </a>
                )}
                <button
                  onClick={() => {
                    setSelected(null);
                    setResolutionNotes('');
                  }}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium w-full sm:w-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

export default AdminGrievancesPage;
